import React from 'react';
import { useCallback, useEffect, useMemo, memo } from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
} from 'reactflow';

import 'reactflow/dist/style.css';
import { useExamSessionStore, getStore } from '../../../stores/StoreContext';
import CustomNode from './CustomNode';
import { layoutNodes } from './layoutNodes';

const JourneySteps = () => {
  // Define custom node types - memoize to prevent unnecessary re-renders
  const nodeTypes = useMemo(
    () => ({
      customNode: CustomNode,
    }),
    []
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Only re-render when journey changes
  const journey = useExamSessionStore((state) => state.journey);
  const ExamSession = getStore('examSessionStore');

  // Memoize the node and edge creation logic to prevent unnecessary calculations
  useEffect(() => {
    // Create a function to build nodes and edges to make the code more maintainable
    const buildNodesAndEdges = () => {
      const newNodes = [];
      const newEdges = [];

      // Find the current step (the last step with a startTime but no completedTime)
      const currentStepId = journey.steps?.findIndex(
        (step) => step.startTime && !step.completedTime
      );

      // loop the step array.
      journey.steps?.forEach((step, index) => {
        //console.log(step);
        // create a node for each step.
        if (step.type !== 'silent') {
          const node = {
            id: step.journeyStepId.toString(),
            //position: { x: 10, y: newNodes.length * 80 + 3 },
            type: 'customNode', // Use our custom node type
            data: {
              label: `${step.name} (${step.journeyStepId})
              ${step.fromJourneyStepId} -> ${step.toJourneyStepId}
              `,
              stepId: step.stepId,
              stepData: step,
              isCurrentStep: index === currentStepId,
            },
            draggable: false, // Disable dragging
            connectable: false, // Disable connections
          };

          // Style based on step status
          if (step.completedTime) {
            node.style = {
              borderColor: '#00ac18', // Success green
            };
          } else if (index === currentStepId) {
            node.style = {
              borderColor: '#0374B5', // Brand blue
              borderWidth: 2,
            };
          }

          newNodes.push(node);

          if (newNodes.length > 1) {
            const edge = {
              id: `e${step.fromJourneyStepId}-${step.toJourneyStepId}`,
              source: step.fromJourneyStepId?.toString(),
              target: step.toJourneyStepId?.toString(),
              markerEnd: { type: MarkerType.ArrowClosed },
              style: {
                strokeWidth: 2,
              },
            };
            newEdges.push(edge);
          }
        }
      });

      const { nodes, edges } = layoutNodes(newNodes, newEdges, 'TB');
      //console.log(nodes, edges);
      return { nodes, edges };
    };

    // Only update state if we have steps
    if (journey.steps?.length) {
      const { nodes, edges } = buildNodesAndEdges();
      setNodes(nodes);
      setEdges(edges);
    }
  }, [journey, setNodes, setEdges]);

  // Memoize the connect handler to prevent unnecessary re-renders
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Memoize the container style to prevent unnecessary re-renders
  const containerStyle = useMemo(
    () => ({
      height: `${Math.max(nodes.length * 80, 200)}px`,
      //width: '240px',
    }),
    [nodes.length]
  );

  // Memoize the ReactFlow props to prevent unnecessary re-renders
  const reactFlowProps = useMemo(
    () => ({
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onConnect,
      nodesDraggable: false,
      nodesConnectable: false,
      elementsSelectable: true,
      panOnDrag: true,
      panOnScroll: true,
      zoomOnScroll: false,
      zoomOnDoubleClick: false,
      zoomOnPinch: false,
      // Remove fitViewOnInit from here to prevent it from being passed to DOM elements
      nodeTypes,
    }),
    [nodes, edges, onNodesChange, onEdgesChange, onConnect, nodeTypes]
  );

  return (
    <div style={containerStyle}>
      {/* Add fitViewOnInit directly to ReactFlow component */}
      <ReactFlow {...reactFlowProps}>
        <Background color="#f8f9fa" gap={16} />
      </ReactFlow>
    </div>
  );
};

// Wrap with memo to prevent unnecessary re-renders
export default memo(JourneySteps);
